import type { JSONContent } from "@tiptap/core"
import { acceptJsonHeaders, csrfHeaders, handleJson, jsonHeaders } from "@/lib/json-request"
import type { ShotFrameSource } from "@/lib/workspace-api"

export type PropReferenceVersionRow = {
  id: number
  state_id: number | null
  preview_url: string | null
  download_url?: string | null
  origin: string
  created_at: string
  active: boolean
}

export type PropPromptPreview = {
  system: string
  user: string
  state_id: number | null
}

export type PropVideoReferenceSource = "reference_sheet" | "front_view"

/** props.generation_settings: ракурсы раскладки и какая картинка уходит в кадр и видео шота. */
export type PropGenerationSettings = {
  layout_views: string[]
  video_reference_source: PropVideoReferenceSource
}

export type PropWritePayload = {
  name?: string
  profile_text?: string | null
  profile_doc?: JSONContent | null
  task_id?: number | null
  generation_settings?: Partial<PropGenerationSettings>
}

export async function createProp(projectId: number, payload: PropWritePayload) {
  const res = await fetch(`/projects/${projectId}/workspace/props`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ prop: payload }),
  })
  return handleJson<{ ok: boolean; prop_id: number; message?: string }>(res)
}

export async function updateProp(projectId: number, propId: number, payload: Partial<PropWritePayload>) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ prop: payload }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function deleteProp(projectId: number, propId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function previewReferencePrompt(
  projectId: number,
  propId: number,
  stateId: number | null,
): Promise<PropPromptPreview> {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/preview_reference_prompt`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ state_id: stateId }),
  })
  return handleJson<PropPromptPreview>(res)
}

export async function generatePropReference(
  projectId: number,
  propId: number,
  stateId: number | null,
  opts?: { correctionPrompt?: string; sourceVersionId?: number },
) {
  const body: Record<string, unknown> = { state_id: stateId }
  if (opts?.correctionPrompt) body.correction_prompt = opts.correctionPrompt
  if (opts?.sourceVersionId) body.source_version_id = opts.sourceVersionId
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/generate_reference`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function registerPropSeedanceAsset(projectId: number, propId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/register_seedance_asset`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({}),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function previewFrontViewPrompt(projectId: number, propId: number): Promise<PropPromptPreview> {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/preview_front_view_prompt`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({}),
  })
  return handleJson<PropPromptPreview>(res)
}

export async function generatePropFrontView(
  projectId: number,
  propId: number,
  opts?: { correctionPrompt?: string; sourceVersionId?: number },
) {
  const body: Record<string, unknown> = {}
  if (opts?.correctionPrompt) body.correction_prompt = opts.correctionPrompt
  if (opts?.sourceVersionId) body.source_version_id = opts.sourceVersionId
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/generate_front_view`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function addReferenceInput(
  projectId: number,
  propId: number,
  file: File,
  stateId?: number | null,
  meta?: { sourceReferenceKey?: string },
) {
  const form = new FormData()
  form.append("file", file)
  if (stateId) form.append("state_id", String(stateId))
  if (meta?.sourceReferenceKey) form.append("source_reference_key", meta.sourceReferenceKey)
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/add_reference_input`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

/** Референс из Библиотеки: сервер берёт существующий блоб, файл не копируется. */
export async function addReferenceInputFromSource(
  projectId: number,
  propId: number,
  source: ShotFrameSource,
  stateId?: number | null,
) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/add_reference_input`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source, state_id: stateId ?? undefined }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function removeReferenceInput(
  projectId: number,
  propId: number,
  blobSignedId: string,
  stateId?: number | null,
) {
  const body: Record<string, unknown> = { blob_signed_id: blobSignedId }
  if (stateId) body.state_id = stateId
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/remove_reference_input`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function createState(
  projectId: number,
  propId: number,
  payload: { name: string; description?: string | null },
) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/states`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ state: payload }),
  })
  return handleJson<{ ok: boolean; state_id: number; message?: string }>(res)
}

export async function updateState(
  projectId: number,
  propId: number,
  stateId: number,
  payload: { name?: string; description?: string | null },
) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/states/${stateId}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ state: payload }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function deleteState(projectId: number, propId: number, stateId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/states/${stateId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function listReferenceVersions(
  projectId: number,
  propId: number,
  stateId: number | null | undefined,
): Promise<PropReferenceVersionRow[]> {
  const params = new URLSearchParams()
  if (stateId === null) params.set("state_id", "")
  else if (typeof stateId === "number") params.set("state_id", String(stateId))
  const qs = params.toString() ? `?${params.toString()}` : ""
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/reference_versions${qs}`, {
    headers: acceptJsonHeaders(),
    credentials: "same-origin",
  })
  const data = await handleJson<{ versions: PropReferenceVersionRow[] }>(res)
  return data.versions
}

export async function uploadReferenceVersion(projectId: number, propId: number, file: File, stateId: number | null) {
  const form = new FormData()
  form.append("file", file)
  if (stateId !== null) form.append("state_id", String(stateId))

  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/reference_versions`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; version: PropReferenceVersionRow }>(res)
}

export async function createPropReferenceVersionFromSource(
  projectId: number,
  propId: number,
  source: ShotFrameSource,
  stateId: number | null,
) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/reference_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source, state_id: stateId ?? undefined }),
  })
  return handleJson<{ ok: boolean; version: PropReferenceVersionRow }>(res)
}

export async function clearPropReferenceVersion(projectId: number, propId: number, opts?: { stateId?: number | null }) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/reference_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      clear: true,
      state_id: opts?.stateId ?? undefined,
    }),
  })
  return handleJson<{ ok: boolean; version: PropReferenceVersionRow }>(res)
}

export async function activateReferenceVersion(projectId: number, propId: number, versionId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/reference_versions/${versionId}/activate`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({}),
  })
  return handleJson<{ ok: boolean; version: PropReferenceVersionRow }>(res)
}

export async function deleteReferenceVersion(projectId: number, propId: number, versionId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/reference_versions/${versionId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean }>(res)
}

export type PropFrontViewVersionRow = {
  id: number
  preview_url: string | null
  download_url?: string | null
  origin: string
  created_at: string
  active: boolean
}

export async function listFrontViewVersions(projectId: number, propId: number): Promise<PropFrontViewVersionRow[]> {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/front_view_versions`, {
    headers: acceptJsonHeaders(),
    credentials: "same-origin",
  })
  const data = await handleJson<{ versions: PropFrontViewVersionRow[] }>(res)
  return data.versions
}

export async function uploadFrontViewVersion(projectId: number, propId: number, file: File) {
  const form = new FormData()
  form.append("file", file)

  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/front_view_versions`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; version: PropFrontViewVersionRow }>(res)
}

export async function createFrontViewVersionFromSource(projectId: number, propId: number, source: ShotFrameSource) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/front_view_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source }),
  })
  return handleJson<{ ok: boolean; version: PropFrontViewVersionRow }>(res)
}

export async function clearPropFrontViewVersion(projectId: number, propId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/front_view_versions`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ clear: true }),
  })
  return handleJson<{ ok: boolean; version: PropFrontViewVersionRow }>(res)
}

export async function activateFrontViewVersion(projectId: number, propId: number, versionId: number) {
  const res = await fetch(
    `/projects/${projectId}/workspace/props/${propId}/front_view_versions/${versionId}/activate`,
    {
      method: "PATCH",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({}),
    },
  )
  return handleJson<{ ok: boolean; version: PropFrontViewVersionRow }>(res)
}

export async function deleteFrontViewVersion(projectId: number, propId: number, versionId: number) {
  const res = await fetch(`/projects/${projectId}/workspace/props/${propId}/front_view_versions/${versionId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean }>(res)
}
